import { useEffect, useMemo, useState } from 'react';
import { useLocation } from '@remix-run/react';
import { cn } from '@rocco/utils/cn';

interface TitleSectionProps {
    /** The category name. */
    category: string;
    /** The root category name. */
    rootCategory?: string;
    /** The description text. */
    categoryDescription: string;
    /** The number of items in the category. */
    itemsInCategory: number;
    /** The breadcrumbs data. */
    breadcrumbs?: { href: string; label: string }[];
    /** Show the category title. Defaults to `true`. */
    showCategoryTitle?: boolean;
    /** The category is the root category. Defaults to `false`. */
    isRoot?: boolean;
}

/**
 * The title header section of the directory pages (professionals, products, projects).
 */
export const TitleSectionDesktop = ({
    category,
    rootCategory,
    categoryDescription,
    showCategoryTitle = true,
    isRoot = false,
}: TitleSectionProps) => {
    const [isExpandDescription, setIsExpandDescription] = useState(false);

    const location = useLocation();

    useEffect(() => {
        setIsExpandDescription(false);
    }, [location.pathname]);

    const escapeHtml = (str: string) =>
        str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

    const strippedDesc = useMemo(() => {
        return categoryDescription
            .replace(/<p[^>]*>/i, '')
            .replace(/<\/p>/i, '');
    }, [categoryDescription]);

    const descriptionHtml = useMemo(() => {
        if (!isRoot) {
            return `<h1 class="inline text-[0.875rem] font-medium text-black">${escapeHtml(
                category,
            )}</h1> - ${strippedDesc}`;
        }
        return `${strippedDesc}`;
    }, [category, strippedDesc, isRoot]);

    return (
        <div className="mb-28 px-12.5 pt-30">
            <div className="flex items-start justify-between">
                <div className={cn('w-full max-w-120 pr-18')}>
                    <div
                        className={cn(
                            'font-ap-body w-full text-[0.875rem] font-normal leading-[110.9%] text-[#767676]',
                        )}
                    >
                        <div
                            className={cn(
                                'mt-8 cursor-pointer md:min-w-120 md:max-w-120',
                                !isExpandDescription && 'line-clamp-6',
                            )}
                            dangerouslySetInnerHTML={{
                                __html: descriptionHtml,
                            }}
                            onClick={() =>
                                setIsExpandDescription((prev) => !prev)
                            }
                        />
                    </div>
                </div>

                {showCategoryTitle && (
                    <div
                        className="relative -mt-2 h-fit w-full text-right transition-all duration-200"
                        style={{ contain: 'layout', minHeight: '13rem' }}
                    >
                        {isRoot ? (
                            <h1 className="inline font-display text-50 font-semibold leading-[0.8] tracking-[-4px] text-[#323232] transition-all duration-200">
                                {rootCategory ?? category}
                            </h1>
                        ) : (
                            <div
                                // Use before pseudo element for SEO reasons.
                                className="inline before:pointer-events-none before:font-display before:text-50 before:font-semibold before:leading-[0.8] before:tracking-[-4px] before:text-[#323232] before:transition-all before:duration-200 before:content-[attr(data-giant-text-content)]"
                                aria-hidden="true"
                                data-giant-text-content={
                                    rootCategory ?? category
                                }
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
